/**
 * Monetag rewarded interstitial — the "watch an ad, then download unlocks" gate.
 * Inert until VITE_MONETAG_ZONE is set (create a Rewarded Interstitial zone in
 * Monetag → paste its zone id). The SDK exposes a global `show_<zone>()` that
 * returns a Promise resolving when the user finishes the ad.
 */
const ZONE = import.meta.env.VITE_MONETAG_ZONE as string | undefined;

export const isMonetagConfigured = (): boolean => Boolean(ZONE);

let injected = false;

function ensureSdk(): void {
  if (injected || !ZONE) return;
  injected = true;
  const script = document.createElement('script');
  script.src = 'https://libtl.com/sdk.js';
  script.dataset.zone = ZONE;
  script.dataset.sdk = `show_${ZONE}`;
  document.head.appendChild(script);
}

/**
 * Show the rewarded ad and resolve when the user finishes it. Rejects if the
 * SDK never becomes ready (ad blocker, network) so callers can fall back to
 * letting the download proceed rather than trapping the user.
 */
export function showRewardedAd(): Promise<void> {
  if (!ZONE) return Promise.reject(new Error('monetag not configured'));
  ensureSdk();
  const fnName = `show_${ZONE}`;

  return new Promise((resolve, reject) => {
    const started = Date.now();
    const attempt = () => {
      const fn = (window as unknown as Record<string, ((...a: unknown[]) => Promise<unknown>) | undefined>)[
        fnName
      ];
      if (typeof fn === 'function') {
        Promise.resolve(fn())
          .then(() => resolve())
          .catch(reject);
      } else if (Date.now() - started < 8000) {
        window.setTimeout(attempt, 300);
      } else {
        reject(new Error('monetag sdk unavailable'));
      }
    };
    attempt();
  });
}
